import { titleCase } from '../titleCase.ts';
import { PHASE_KEYS, earlierPhase, phaseOfNode, phasesOf, type PhaseKey } from './phases.ts';
import type { ProcessKind } from './gates.ts';
import { availableTo, type NodeKind, type ProcessNodeDefT, type ProcessTreeDoc, type StepKind } from './schema.ts';

/**
 * A project's process: the steps it added from the master tree (plus the ones
 * it grew itself) and the waits between them. Pure: the API loads and stores
 * it, the web previews changes with the same functions before asking for them.
 *
 * A job is the steps it has (tech-tree spec v3 §8): each comes after the
 * nearest steps before it in the tree that the job has, passing over the ones
 * it does not; nothing comes with a step but its own list.
 *
 * Steps are addressed by id. A step from the tree carries the tree's key; a
 * repeated one (each named copy) also carries its instance name; a step the
 * project grew has no key.
 */

export type StepState = 'not_started' | 'available' | 'in_progress' | 'done';
/** A wait: the only link between a job's steps (the older kind, "informs", went with the wipe of 2026-10-07). */
export type LinkKind = 'requires';

export interface ProcessStep {
  id: string;
  key: string | null;
  instance: string | null;
  kind: StepKind;
  title: string;
  parentId: string | null;
  state: StepState;
  /** Calendar days it takes; null when unknown (planned as a week, for the order alone). */
  days: number | null;
  /** Turned off but kept, because it has history. */
  off: boolean;
  startedOn: string | null;
  doneOn: string | null;
  /**
   * The phase a step sits in on the job when it is not the tree's: the one a job's own step was added to (design study
   * 12), or the phase under way a step of the tree landed in, late, its own phase closed (design study 21, land A), or
   * the one a task is set to be done in on the job, its step's or an earlier one (design study 31, slice 4).
   * None for a step of the tree in its own phase, as the engine keeps it; a process read back from the API carries
   * every step's phase as the API read it (`processFromDto`), which reads the same.
   */
  phase?: PhaseKey | null;
}

/** `stepId` waits on `needsId`. */
export interface StepLink {
  stepId: string;
  needsId: string;
  kind: LinkKind;
}

export interface ProjectProcess {
  kind: ProcessKind;
  steps: ProcessStep[];
  links: StepLink[];
  /**
   * The tree's steps left out of this job (study 07): never offered again
   * unless put back, and passed over, so what comes after one is offered in
   * its place.
   */
  out?: string[];
}

/** What adding a step of the tree would add, or bring back, before it is done. */
export interface EnablePlan {
  /** Tree keys to add, parents first, then in tree order. */
  add: string[];
  /** Off steps (kept for their history) that come back on. */
  restore: string[];
}

export interface DisablePlan {
  /** Ids that go off with it: itself and what is inside it. */
  off: string[];
  /** Of those, the ones with history: they are kept, turned off, never deleted. */
  keep: string[];
  /** Why it cannot be turned off, if it cannot. */
  blocked: string | null;
}

export const stepKey = (s: Pick<ProcessStep, 'key' | 'instance'>) => (s.key === null ? null : s.instance ? `${s.key}#${s.instance}` : s.key);

/** The tree, indexed. */
export class Tree {
  readonly doc: ProcessTreeDoc;
  readonly byKey: Map<string, ProcessNodeDefT>;
  readonly order: Map<string, number>;
  readonly children: Map<string | undefined, ProcessNodeDefT[]>;
  /** Steps that name this key in their `before`. */
  readonly beforeOf: Map<string, string[]>;

  constructor(doc: ProcessTreeDoc) {
    this.doc = doc;
    this.byKey = new Map(doc.nodes.map((n) => [n.key, n]));
    this.order = new Map(doc.nodes.map((n, i) => [n.key, i]));
    this.children = new Map();
    this.beforeOf = new Map();
    for (const n of doc.nodes) {
      this.children.set(n.parent, [...(this.children.get(n.parent) ?? []), n]);
      for (const b of n.before ?? []) this.beforeOf.set(b, [...(this.beforeOf.get(b) ?? []), n.key]);
    }
  }

  node(key: string): ProcessNodeDefT | undefined {
    return this.byKey.get(key);
  }

  kids(key: string | undefined): ProcessNodeDefT[] {
    return this.children.get(key) ?? [];
  }

  /** The step a child belongs to, up to the top: a repeated step's children live in each copy. */
  ancestors(key: string): string[] {
    const out: string[] = [];
    for (let p = this.byKey.get(key)?.parent; p !== undefined; p = this.byKey.get(p)?.parent) out.push(p);
    return out;
  }

  /** The repeatable step a key sits under (itself included), if any. */
  repeatableRoot(key: string): string | null {
    for (const k of [key, ...this.ancestors(key)]) if (this.byKey.get(k)?.repeatable) return k;
    return null;
  }
}

/**
 * Adding a step of the tree: its parents come with it (a task never stands
 * without its step), and so do the steps in its own list that are basic for
 * this kind of job. Nothing else: what comes before it in the tree is added by
 * hand, or not at all.
 */
export function planEnable(tree: Tree, p: ProjectProcess, key: string): EnablePlan {
  const def = tree.node(key);
  if (!def) throw new Error(`No step ${key} in the tree`);
  if (def.repeatable) throw new Error(`${titleCase(def.title)} is added by name, as many times as needed`);
  const present = new Map(p.steps.filter((s) => s.key !== null && s.instance === null).map((s) => [s.key!, s]));
  const want = new Set<string>();
  const visit = (k: string) => {
    if (want.has(k)) return;
    const d = tree.node(k);
    if (!d || !availableTo(d, p.kind) || tree.repeatableRoot(k)) return;
    const s = present.get(k);
    if (s && !s.off) return;
    want.add(k);
    if (d.parent) visit(d.parent);
    for (const c of tree.kids(k)) if (c.basic?.includes(p.kind)) visit(c.key);
  };
  visit(key);
  const depth = (k: string) => tree.ancestors(k).length;
  const add = [...want].filter((k) => !present.has(k)).sort((a, b) => depth(a) - depth(b) || tree.order.get(a)! - tree.order.get(b)!);
  const restore = [...want].filter((k) => present.get(k)?.off).map((k) => present.get(k)!.id);
  return { add, restore };
}

/**
 * Taking a step out of a job takes what is inside it, never what comes after
 * it (the order closes up behind it, `disable`). A sign-off once passed stays,
 * as does work under way or done. Steps with history are kept, turned off;
 * the rest are removed.
 */
export function planDisable(p: ProjectProcess, id: string, hasHistory: (id: string) => boolean): DisablePlan {
  const byId = new Map(p.steps.map((s) => [s.id, s]));
  if (!byId.get(id)) throw new Error(`No step ${id}`);
  const off = new Set<string>();
  const visit = (sid: string) => {
    if (off.has(sid)) return;
    const s = byId.get(sid);
    if (!s || s.off) return;
    off.add(sid);
    for (const c of p.steps) if (c.parentId === sid) visit(c.id);
  };
  visit(id);
  const passedGate = [...off].map((x) => byId.get(x)!).find((s) => s.kind === 'gate' && s.state === 'done');
  if (passedGate) return { off: [], keep: [], blocked: `${passedGate.title} has been passed: it stays.` };
  const started = [...off].map((x) => byId.get(x)!).find((s) => s.kind !== 'gate' && s.state !== 'not_started' && s.state !== 'available');
  if (started) return { off: [], keep: [], blocked: `${titleCase(started.title)} is ${started.state === 'done' ? 'done' : 'under way'}, so it stays.` };
  // A step kept for its history keeps the steps that hold it, so it is never orphaned; so does one turned off and kept
  // before (it is not in `off`, but its row stays, and deleting what holds it would delete it with it).
  const keep = new Set([...off].filter(hasHistory));
  const stays = (sid: string) => !off.has(sid) || keep.has(sid);
  for (let grew = true; grew; ) {
    grew = false;
    for (const s of p.steps) {
      if (!s.parentId || !stays(s.id) || stays(s.parentId)) continue;
      keep.add(s.parentId);
      grew = true;
    }
  }
  return { off: [...off], keep: [...keep], blocked: null };
}

/** The live steps from the tree, by key (named copies aside). */
function liveByKey(p: ProjectProcess): Map<string, string> {
  return new Map(p.steps.filter((s) => !s.off && s.key !== null && s.instance === null).map((s) => [s.key!, s.id]));
}

/**
 * For each key, the nearest steps before it in the tree that pass a test,
 * walking back over the ones that do not but may be passed (`through`). The
 * tree's order never loops (schema.ts), and a walk never revisits a key.
 */
function nearestBefore(tree: Tree, stop: (k: string) => boolean, through: (k: string) => boolean): (key: string) => { found: Set<string>; passed: Set<string> } {
  const memo = new Map<string, { found: Set<string>; passed: Set<string> }>();
  const walking = new Set<string>();
  const walk = (key: string) => {
    const got = memo.get(key);
    if (got) return got;
    const found = new Set<string>();
    const passed = new Set<string>();
    walking.add(key);
    for (const d of tree.beforeOf.get(key) ?? []) {
      if (stop(d)) found.add(d);
      else if (through(d) && !walking.has(d)) {
        const r = walk(d);
        passed.add(d);
        for (const x of r.found) found.add(x);
        for (const x of r.passed) passed.add(x);
      }
    }
    walking.delete(key);
    const r = { found, passed };
    memo.set(key, r);
    return r;
  };
  return walk;
}

/**
 * The waits the tree gives a job: each step comes after the nearest steps
 * before it in the tree that the job has, passing over the ones it does not
 * (left out, or not added), so the tree's order holds among whatever the job
 * has. A step inside another is held by it.
 */
export function treeLinks(tree: Tree, p: ProjectProcess): StepLink[] {
  const on = liveByKey(p);
  const before = nearestBefore(tree, (k) => on.has(k), () => true);
  const out: StepLink[] = [];
  for (const [key, id] of on) {
    for (const d of before(key).found) {
      const needsId = on.get(d)!;
      if (needsId !== id) out.push({ stepId: id, needsId, kind: 'requires' });
    }
  }
  return dedupeLinks(out);
}

/** The waits a new copy of a repeatable step brings: its own list's order, and what it comes before and after in the tree. */
export function instanceLinks(tree: Tree, p: ProjectProcess, rootKey: string, instance: string): StepLink[] {
  const inst = new Map(p.steps.filter((s) => s.instance === instance && s.key !== null).map((s) => [s.key!, s.id]));
  const ids = liveByKey(p);
  const resolve = (k: string) => inst.get(k) ?? ids.get(k);
  const out: StepLink[] = [];
  const rootId = inst.get(rootKey);
  if (rootId) {
    for (const s of p.steps) {
      if (s.off || s.key === null || s.instance !== null) continue;
      if (tree.node(s.key)?.before?.includes(rootKey)) out.push({ stepId: rootId, needsId: s.id, kind: 'requires' });
    }
  }
  for (const [k, id] of inst) {
    for (const b of tree.node(k)?.before ?? []) {
      const n = resolve(b);
      if (n) out.push({ stepId: n, needsId: id, kind: 'requires' });
    }
  }
  return dedupeLinks(out);
}

/** Links without repeats: the same step, waiting on the same step, once. */
export function dedupeLinks(links: StepLink[]): StepLink[] {
  const seen = new Set<string>();
  return links.filter((l) => {
    const k = `${l.stepId}|${l.needsId}|${l.kind}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/** Is `id` inside `holder`, at any depth? */
export function isWithin(p: ProjectProcess, id: string, holder: string): boolean {
  return holdersOf(p, id).includes(holder);
}

/** The steps that hold a step, nearest first; a parent loop (never written, but never trusted) ends the walk. */
export function holdersOf(p: ProjectProcess, id: string): string[] {
  const byId = new Map(p.steps.map((s) => [s.id, s]));
  const out: string[] = [];
  for (let up = byId.get(id)?.parentId ?? null; up !== null && up !== id && !out.includes(up); up = byId.get(up)?.parentId ?? null) out.push(up);
  return out;
}

/** A step that holds what holds it: the first one found, or null. */
export function findParentLoop(p: ProjectProcess): string | null {
  const byId = new Map(p.steps.map((s) => [s.id, s]));
  for (const s of p.steps) {
    const seen = new Set<string>([s.id]);
    for (let up = s.parentId; up !== null; up = byId.get(up)?.parentId ?? null) {
      if (seen.has(up)) return s.id;
      seen.add(up);
    }
  }
  return null;
}

/**
 * The order as the plan reads it (schedule.ts): a step that holds steps is a
 * start before them and an end after them, and a link runs from the end of
 * what is waited on to the start of what waits. A task done in an earlier
 * phase than the step holding it (design study 31, slice 4), as the job's
 * `phases` read (core `projectPhases`), is before its step's end and not after
 * its start; with no phases given, every task is after its step's start.
 */
function orderGraph(p: ProjectProcess, phases: ReadonlyMap<string, PhaseKey>) {
  const live = p.steps.filter((s) => !s.off);
  const byId = new Map(live.map((s) => [s.id, s]));
  const holds = new Set(live.filter((s) => s.parentId !== null && byId.has(s.parentId)).map((s) => s.parentId!));
  const entry = (id: string) => (holds.has(id) ? `${id}:start` : id);
  const exit = (id: string) => (holds.has(id) ? `${id}:end` : id);
  const next = new Map<string, string[]>();
  const add = (a: string, b: string) => {
    if (a !== b) next.set(a, [...(next.get(a) ?? []), b]);
  };
  for (const s of live) {
    if (s.parentId === null || !byId.has(s.parentId)) continue;
    if (!earlierPhase(phases, s.id, s.parentId)) add(`${s.parentId}:start`, entry(s.id));
    add(exit(s.id), `${s.parentId}:end`);
  }
  for (const l of p.links) {
    if (byId.has(l.stepId) && byId.has(l.needsId)) add(exit(l.needsId), entry(l.stepId));
  }
  return { byId, entry, exit, next };
}

const stepOf = (k: string) => k.replace(/:(start|end)$/, '');

/** No phases given: every task after its step's start, as the order read before a task could be done early. */
const NO_PHASES: ReadonlyMap<string, PhaseKey> = new Map();

/**
 * A loop in the order, if there is one: the steps on it, in order. `phases`, the job's (core `projectPhases`): a task
 * done early is not after its step's start (design study 31, slice 4).
 */
export function findLoop(p: ProjectProcess, phases: ReadonlyMap<string, PhaseKey> = NO_PHASES): string[] | null {
  const { next } = orderGraph(p, phases);
  const state = new Map<string, 1 | 2>();
  const trail: string[] = [];
  const visit = (k: string): string[] | null => {
    state.set(k, 1);
    trail.push(k);
    for (const m of next.get(k) ?? []) {
      const st = state.get(m);
      if (st === 1) return trail.slice(trail.indexOf(m));
      if (st === undefined) {
        const found = visit(m);
        if (found) return found;
      }
    }
    trail.pop();
    state.set(k, 2);
    return null;
  };
  for (const k of next.keys()) {
    if (state.has(k)) continue;
    const found = visit(k);
    if (found) return [...new Set(found.map(stepOf))];
  }
  return null;
}

/**
 * For one step, which steps it could not wait on without closing a loop:
 * everything that already comes after it, and what holds it or sits inside it.
 * One pass, so a list of candidates can be filtered at once. `phases` as `findLoop` reads them.
 */
export function loopsIfWaits(p: ProjectProcess, stepId: string, phases: ReadonlyMap<string, PhaseKey> = NO_PHASES): (needsId: string) => boolean {
  const { next, entry, exit } = orderGraph(p, phases);
  const reach = new Set<string>();
  const stack = [entry(stepId)];
  while (stack.length) {
    const k = stack.pop()!;
    if (reach.has(k)) continue;
    reach.add(k);
    stack.push(...(next.get(k) ?? []));
  }
  return (needsId: string) =>
    needsId === stepId || reach.has(exit(needsId)) || reach.has(needsId) || isWithin(p, needsId, stepId) || isWithin(p, stepId, needsId);
}

/** Would `stepId` waiting on `needsId` close a loop, or tie a step to what holds it? `phases` as `findLoop` reads them. */
export function wouldLoop(p: ProjectProcess, stepId: string, needsId: string, phases: ReadonlyMap<string, PhaseKey> = NO_PHASES): boolean {
  return loopsIfWaits(p, stepId, phases)(needsId);
}

/** A step of the tree that could be added next, and where it would hang off what the job has. */
export interface StepOption {
  key: string;
  title: string;
  kind: NodeKind;
  /** The step it would sit inside, if any (a tree key). */
  parent: string | null;
  /** Steps of the job it comes after: where it would appear. */
  after: string[];
  repeatable: boolean;
  why: string | null;
  /** Position in the walk: the order of work, the earliest place in the flow first. */
  rank: number;
  /** The steps left out of the job it comes after them through: it is offered in their place. */
  via?: string[];
}

/** Each live step's place in a topological order of the whole process (waits, parents before children). */
function topoRank(p: ProjectProcess): Map<string, number> {
  const live = p.steps.filter((s) => !s.off);
  const liveIds = new Set(live.map((s) => s.id));
  const preds = new Map<string, Set<string>>(live.map((s) => [s.id, new Set<string>()]));
  for (const l of p.links) if (liveIds.has(l.stepId) && liveIds.has(l.needsId)) preds.get(l.stepId)!.add(l.needsId);
  for (const s of live) if (s.parentId && liveIds.has(s.parentId)) preds.get(s.id)!.add(s.parentId);
  const rank = new Map<string, number>();
  const visiting = new Set<string>();
  const visit = (id: string): number => {
    if (rank.has(id)) return rank.get(id)!;
    if (visiting.has(id)) return 0;
    visiting.add(id);
    let r = 0;
    for (const q of preds.get(id) ?? []) r = Math.max(r, visit(q) + 1);
    visiting.delete(id);
    rank.set(id, r);
    return r;
  };
  for (const s of live) visit(s.id);
  return rank;
}

/**
 * A step's own list (design study 07): the items of a step on the job that are not on it, offered in it. Never what the
 * job has or left out, never in a step that repeats. The rest of the tree is added from its phase's list, top down
 * (design study 31, slice 2), never offered after a step.
 */
export function ownListOffers(tree: Tree, p: ProjectProcess): StepOption[] {
  const on = liveByKey(p);
  const out = new Set(p.out ?? []);
  const order = topoRank(p);
  const rankOf = (k: string) => order.get(on.get(k) ?? '') ?? 0;
  const offers: StepOption[] = [];
  for (const d of tree.doc.nodes) {
    if (d.parent === undefined || on.has(d.key) || out.has(d.key) || !availableTo(d, p.kind)) continue;
    if (!on.has(d.parent) || tree.repeatableRoot(d.parent)) continue;
    offers.push({ key: d.key, title: d.title, kind: d.kind, parent: d.parent, after: [d.parent], repeatable: d.repeatable ?? false, why: d.why ?? null, rank: rankOf(d.parent) });
  }
  return offers.sort((a, b) => a.rank - b.rank || tree.order.get(a.key)! - tree.order.get(b.key)!);
}

/**
 * What ticking steps brings with them (design study 31, slice 2; Jason, 2026-10-29: working back "only makes sense if
 * the dependencies are correct"): every step of the tree that comes before any of `keys`, however far back, that the job
 * has not got, the way back stopping at each step the job has (at worst the tree's start, which every job has) and
 * running on through a step filed away, listed as filed so adding puts it back. A step no job of this kind has, a step's
 * own list and a step that repeats are passed over, the way back running on through them. Each step once, in the tree's
 * order; never one of `keys` itself, which are being added.
 */
export function missingNeeds(tree: Tree, p: ProjectProcess, keys: string[]): { key: string; filed: boolean }[] {
  const on = liveByKey(p);
  const out = new Set(p.out ?? []);
  const asked = new Set(keys);
  const listed = (k: string) => {
    const d = tree.node(k);
    return Boolean(d && d.parent === undefined && !d.repeatable && availableTo(d, p.kind));
  };
  const found = new Set<string>();
  const seen = new Set<string>();
  const walk = (k: string) => {
    for (const b of tree.beforeOf.get(k) ?? []) {
      if (seen.has(b)) continue;
      seen.add(b);
      if (on.has(b)) continue;
      if (listed(b) && !asked.has(b)) found.add(b);
      walk(b);
    }
  };
  for (const k of keys) walk(k);
  return [...found].sort((a, b) => (tree.order.get(a) ?? 0) - (tree.order.get(b) ?? 0)).map((key) => ({ key, filed: out.has(key) }));
}

/**
 * The phases closed on a job, each with the day it closed (design study 21, land A): a phase is closed once its closing
 * step on the job is done, and so is every phase before a closed one (the gates are passed in order, so one passed later
 * closes what came before it, whatever the job had of those), on the first of those days. A phase still open is not in
 * it. Where a step of the tree lands (`landingPhase`) and where a task done early is done once its phase has closed
 * (core `projectPhases`, design study 31, slice 4) read it.
 */
export function phasesClosed(doc: Pick<ProcessTreeDoc, 'phases'>, p: ProjectProcess): Map<PhaseKey, string> {
  const phases = phasesOf(doc);
  const on = liveByKey(p);
  const byId = new Map(p.steps.map((s) => [s.id, s]));
  const out = new Map<PhaseKey, string>();
  let first: string | null = null;
  for (const ph of [...phases].reverse()) {
    const gate = ph.closedBy ? byId.get(on.get(ph.closedBy) ?? '') : undefined;
    if (gate?.state === 'done' && (first === null || (gate.doneOn ?? '') < first)) first = gate.doneOn ?? '';
    if (first !== null) out.set(ph.key, first);
  }
  return out;
}

/**
 * Where a step of the tree lands when it is added (design study 21, land A): in its own phase while that phase is open,
 * its closing gate on the job not done; once its phase has closed (`phasesClosed`), in the phase under way, marked late.
 * The phase under way is the first phase not closed; past the last, the last.
 */
export function landingPhase(tree: Tree, p: ProjectProcess, key: string): { phase: PhaseKey; late: boolean } {
  const phases = phasesOf(tree.doc);
  const home: PhaseKey = phaseOfNode(tree.doc, key) ?? phases[0]?.key ?? PHASE_KEYS[0];
  const closed = phasesClosed(tree.doc, p);
  if (!phases.some((ph) => ph.key === home) || !closed.has(home)) return { phase: home, late: false };
  const under = phases.find((ph) => !closed.has(ph.key))?.key ?? phases.at(-1)!.key;
  return { phase: under, late: under !== home };
}
